/* ============================================================================
   Founder's Story: the two interactive pieces.

     <unc-fs-day>   the Then/Now day chart
     <unc-fs-loop>  the dependency loop

   Loaded only on the page whose template suffix is `founder-story`.

   Both follow the pattern the rest of this theme uses: SELECTION LIVES IN THE
   DOM AND NOWHERE ELSE. `aria-selected` drives every highlight through CSS
   attribute selectors, so there is no parallel state object that can drift out
   of sync with what is on screen.

   Both are also fully server-rendered before this file runs. With JS blocked
   the chart shows the "then" curve with the 3pm moment open, and the loop shows
   stage one. Inert, but correct and readable.
   ============================================================================ */
(function () {
  'use strict';

  /* Shared pause-aware interval from unc-pdp.js: pauses on hover, on
     focus-within, when off-screen, and when the tab is hidden. Exported there
     rather than reimplemented here so there is one set of pause rules.
     The fallback keeps this file working if unc-pdp.js ever stops loading. */
  function autoCycle(host, step, ms) {
    if (typeof window.uncAutoCycle === 'function') return window.uncAutoCycle(host, step, ms);
    var id = setInterval(step, ms);
    return function () {
      clearInterval(id);
    };
  }

  function text(el, value) {
    if (el) el.textContent = value;
  }

  /* --------------------------------------------------------------------------
     <unc-fs-day>

     Two sets of moments are server-rendered, one per mode, and switching mode
     only changes which set is shown. Both curves are always in the DOM and
     crossfade on opacity, so nothing is rebuilt and the shape you are leaving
     stays faintly visible as a reference.

     Each moment carries its own caption in data attributes. That keeps the copy
     in the Liquid where a merchant can edit it, instead of in a JS array that
     would have to be kept in step with the schema.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-fs-day')) {
    customElements.define(
      'unc-fs-day',
      class UncFsDay extends HTMLElement {
        connectedCallback() {
          this.modes = Array.from(this.querySelectorAll('[data-fs-mode]'));
          this.groups = Array.from(this.querySelectorAll('[data-fs-group]'));
          if (!this.modes.length || !this.groups.length) return;

          this.curves = Array.from(this.querySelectorAll('[data-fs-curve]'));
          this.eyebrow = this.querySelector('[data-fs-eyebrow]');
          this.capTime = this.querySelector('[data-fs-captime]');
          this.capTag = this.querySelector('[data-fs-captag]');
          this.capTitle = this.querySelector('[data-fs-captitle]');
          this.capBody = this.querySelector('[data-fs-capbody]');
          this.legendDot = this.querySelector('[data-fs-legenddot]');

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);

          // Adopt whatever the server already marked as current.
          var openMode = this.modes.filter(function (m) {
            return m.getAttribute('aria-selected') === 'true';
          })[0];
          this.setMode((openMode || this.modes[0]).dataset.fsMode, false);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
        }

        onClick(e) {
          var mode = e.target.closest('[data-fs-mode]');
          if (mode && this.contains(mode)) {
            this.setMode(mode.dataset.fsMode, true);
            return;
          }
          var dot = e.target.closest('[data-fs-moment]');
          if (dot && this.contains(dot)) this.select(dot);
        }

        /* Arrow keys walk the moments of the CURRENT mode only. Stepping into a
           hidden group would move focus to something nobody can see. */
        onKeydown(e) {
          var dot = e.target.closest('[data-fs-moment]');
          if (!dot || !this.contains(dot)) return;
          var dots = this.currentDots();
          var i = dots.indexOf(dot);
          var last = dots.length - 1;
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          else return;
          e.preventDefault();
          this.select(dots[next]);
          dots[next].focus();
        }

        currentGroup() {
          var mode = this.dataset.mode;
          return this.groups.filter(function (g) {
            return g.dataset.fsGroup === mode;
          })[0];
        }

        currentDots() {
          var g = this.currentGroup();
          return g ? Array.from(g.querySelectorAll('[data-fs-moment]')) : [];
        }

        setMode(mode, focus) {
          this.dataset.mode = mode;

          this.modes.forEach(function (m) {
            var on = m.dataset.fsMode === mode;
            m.setAttribute('aria-selected', on ? 'true' : 'false');
            m.setAttribute('tabindex', on ? '0' : '-1');
          });

          this.groups.forEach(function (g) {
            var on = g.dataset.fsGroup === mode;
            g.hidden = !on;
          });

          /* The inactive curve stays painted at 0.14 rather than being removed,
             which is what makes the switch read as a comparison. */
          this.curves.forEach(function (c) {
            c.style.opacity = c.dataset.fsCurve === mode ? '1' : '0.14';
          });

          var accent = mode === 'now' ? '#4A90E2' : '#D4AF37';
          if (this.legendDot) this.legendDot.style.background = accent;
          this.querySelectorAll('[data-fs-swatch]').forEach(function (s) {
            s.style.opacity = s.dataset.fsSwatch === mode ? '1' : '0.14';
          });

          if (this.eyebrow) {
            var label = this.eyebrow.dataset[mode === 'now' ? 'fsNowlabel' : 'fsThenlabel'];
            if (label) this.eyebrow.textContent = label;
          }

          // Open whichever moment this mode marks as its default.
          var dots = this.currentDots();
          if (!dots.length) return;
          var preferred = dots.filter(function (d) {
            return d.dataset.fsDefault === 'true';
          })[0];
          this.select(preferred || dots[0]);

          if (focus) {
            var openMode = this.modes.filter(function (m) {
              return m.dataset.fsMode === mode;
            })[0];
            if (openMode) openMode.focus();
          }
        }

        select(dot) {
          if (!dot) return;
          this.currentDots().forEach(function (d) {
            var on = d === dot;
            d.setAttribute('aria-selected', on ? 'true' : 'false');
            d.setAttribute('tabindex', on ? '0' : '-1');
          });
          text(this.capTime, dot.dataset.fsTime || '');
          text(this.capTag, dot.dataset.fsTag || '');
          text(this.capTitle, dot.dataset.fsTitle || '');
          text(this.capBody, dot.dataset.fsBody || '');
        }
      }
    );
  }

  /* --------------------------------------------------------------------------
     <unc-fs-loop>

     Four stages on a ring, advancing on their own until you touch them.

     THE AUTO-ADVANCE STOPS PERMANENTLY ON CLICK, and does not resume. That is
     deliberate and matches the comp: once a reader has chosen a stage to read,
     having the panel move on by itself mid-sentence is hostile.
  -------------------------------------------------------------------------- */
  if (!customElements.get('unc-fs-loop')) {
    customElements.define(
      'unc-fs-loop',
      class UncFsLoop extends HTMLElement {
        connectedCallback() {
          this.stages = Array.from(this.querySelectorAll('[data-fs-stage]'));
          if (this.stages.length < 2) return;

          /* data-fs-out*, not data-fs-stagename: every stage button carries
             data-fs-stagename as its source value, so reusing that name here
             would select a button and textContent would delete its contents. */
          this.outNum = this.querySelector('[data-fs-outname]');
          this.outBody = this.querySelector('[data-fs-outbody]');

          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.addEventListener('click', this.onClick);
          this.addEventListener('keydown', this.onKeydown);

          var open = this.stages.filter(function (s) {
            return s.getAttribute('aria-selected') === 'true';
          })[0];
          this.select(open || this.stages[0]);

          var ms = parseInt(this.dataset.interval || '3600', 10);
          if (ms > 0) {
            var self = this;
            this.stopAuto = autoCycle(
              this,
              function () {
                var i = self.stages.indexOf(
                  self.stages.filter(function (s) {
                    return s.getAttribute('aria-selected') === 'true';
                  })[0]
                );
                self.select(self.stages[(i + 1) % self.stages.length]);
              },
              ms
            );
          }
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
          this.removeEventListener('keydown', this.onKeydown);
          if (this.stopAuto) this.stopAuto();
        }

        halt() {
          if (this.stopAuto) {
            this.stopAuto();
            this.stopAuto = null;
          }
        }

        onClick(e) {
          var stage = e.target.closest('[data-fs-stage]');
          if (!stage || !this.contains(stage)) return;
          this.halt();
          this.select(stage);
        }

        onKeydown(e) {
          var stage = e.target.closest('[data-fs-stage]');
          if (!stage || !this.contains(stage)) return;
          var i = this.stages.indexOf(stage);
          var last = this.stages.length - 1;
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = last;
          else return;
          e.preventDefault();
          this.halt();
          this.select(this.stages[next]);
          this.stages[next].focus();
        }

        select(stage) {
          if (!stage) return;
          this.stages.forEach(function (s) {
            var on = s === stage;
            s.setAttribute('aria-selected', on ? 'true' : 'false');
            s.setAttribute('tabindex', on ? '0' : '-1');
          });
          text(this.outNum, stage.dataset.fsStagename || '');
          text(this.outBody, stage.dataset.fsBody || '');
        }
      }
    );
  }
})();
